<template>
  <div class="results-wrapper">
    <div class="title">
      <h1>
        {{ motivational }}
        <span></span>
      </h1>
    </div>
    <div class="chart" v-bind:style="{ backgroundImage: 'url(' + spotCheckBg + '), none' }">
      <div class="stats-container">
        <h2 class="spotCheckType">{{ spotCheckType }}</h2>
        <ul class="stats">
          <li class="category">
            <span>Time: </span>
            <span class="category-spacer">
              {{ result.min }}
              <span class="statsValueDecorator">min</span>
              {{ result.sec }}
              <span class="statsValueDecorator">sec</span>
            </span>
          </li>
          <li class="category smaller">
            <span class="smaller">
              {{ perMinuteLabel }}:
              <span>({{ perMinuteLabelAbbreviation }})</span>
            </span>
            <span>
              {{ result.wpm }}
            </span>
          </li>
          <li class="category">
            <span>Errors: </span>
            <span>
              {{ result.numErr }}
            </span>
          </li>
          <li class="category">
            <span>Accuracy: </span>
            <span>{{ result.accuracy }}%</span>
          </li>
        </ul>
        <h2 class="myBestTitle">My Best</h2>
        <ul class="myBest">
          <li>
            <span
              class="ribbon"
              :style="{ backgroundImage: 'url(' + ribbon + '), none' }"
              :class="{ show: result.isNewBest }"
            ></span>
            <span class="newBest" :class="{ show: result.isNewBest }">NEW BEST!</span>
          </li>
          <li>
            <span class="heading">{{ perMinuteLabel }}</span>
            <span class="bestWpm">{{ result.bestResultsWPM }}</span>
          </li>
          <li>
            <span class="heading">Accuracy</span>
            <span class="bestAccuracy">{{ result.bestResultsAccuracy }}%</span>
          </li>
        </ul>
      </div>
    </div>
    <div class="spotCheckNext">
      <button @click="redoSpotCheck" v-if="showRetryBtn" class="spotCheckButton pure-button">
        <span v-html="svgTryAgain"></span>
        <span>Try Again</span>
      </button>
      <button
        @click="continueCurriculum"
        v-if="kwtMode !== 'preview'"
        class="spotCheckButton pure-button"
        style="margin-left: 210px"
      >
        <span v-html="svgQuit"></span>
        <span>Skip Ahead</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import spotCheckBg from 'images/spotCheckBg.png';
import ribbon from 'images/spotcheck-results/ribbon.png';
import svgTryAgain from 'assets/svg/spotCheckRetry.svg';
import svgQuit from 'assets/svg/spotCheckQuit.svg';
import KeyboardingAPI from 'src/api';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

export default {
  mixins: [AccessibilityHelper, ErrorLogger],

  data() {
    return {
      perMinuteLabel: 'WPM',
      perMinuteLabelAbbreviation: 'Words Per Minute',
      spotCheckBg,
      ribbon,
      svgTryAgain,
      svgQuit,
      result: {
        min: 0,
        sec: 0,
        wpm: 0,
        numErr: 0,
        accuracy: 0,
        isNewBest: false,
        bestResultsWPM: 0,
        bestResultsAccuracy: 0
      }
    };
  },

  computed: {
    ...mapState('activity', {
      showRetryBtn: state => state.spotCheckRetries > 0,
      isSpotCheck: state => Boolean(state.data.spotCheck),
      activityName: state => state.data.type,
      spotCheckRetries: state => state.spotCheckRetries,
      activityId: state => state.data.activityId,
      activityType: state => state.data.type
    }),

    ...mapState('student', ['username', 'bestResults']),

    ...mapState('student', {
      studentId: state => state.id,
      grade: state => state.grade || '',
      nextPosition: state => state.position.next || {}
    }),

    ...mapState('application', ['kwtMode']),

    motivational() {
      return `Keep up the good work${this.username ? `, ${this.username}!` : '!'}`;
    },

    spotCheckType() {
      switch (this.activityName) {
        case 'ALettersKPM':
          return 'Letters';
        case 'AWordsWPM':
          return 'Words';
        case 'ASentencesWPM':
          return 'Sentences';
        case 'AParagraphWPM':
          return 'Paragraphs';
        default:
          return '';
      }
    }
  },

  mounted() {
    this.clearKeyboardingSegmentAlert();
    this.calculateActivityData();

    if (this.isSpotCheck && this.kwtMode === 'curriculum') {
      this.sendData();
    }
  },

  methods: {
    ...mapMutations('student', ['setCurriculumPosition', 'setNewBestResults']),
    ...mapActions('activity', ['restartActivity']),
    ...mapMutations('activity', ['setSpotCheckRetries']),

    calculateActivityData() {
      const { duration, numberOfChars, errors } = this.getErrorLogger();

      const numErr = errors.length;
      const totChar = numberOfChars + numErr;
      const accuracy = Math.round((100 * numberOfChars) / totChar);

      const { min, sec } = this.secondsToString(duration);

      const wpm = numberOfChars && duration ? Math.round(numberOfChars / 5 / (duration / 60)) : 0;

      const spotCheckType = this.spotCheckType.toLowerCase();

      const previousWpm = this.bestResults.wpm[spotCheckType] || 0;

      const previousAccuracy = this.bestResults.accuracy[spotCheckType] || 0;

      const isNewBest = wpm > previousWpm;

      const bestResultsWPM = isNewBest ? wpm : previousWpm;
      const bestResultsAccuracy = isNewBest ? accuracy : previousAccuracy;

      if (isNewBest) {
        this.setNewBestResults({
          wpm: {
            ...this.bestResults.wpm,
            [spotCheckType]: bestResultsWPM
          },
          accuracy: {
            ...this.bestResults.accuracy,
            [spotCheckType]: bestResultsAccuracy
          }
        });
      }

      this.result = {
        min,
        sec,
        wpm,
        numErr,
        accuracy,
        isNewBest,
        bestResultsWPM,
        bestResultsAccuracy
      };
    },

    secondsToString(time) {
      const hasMinutes = Math.floor(time / 60);
      const min = hasMinutes > 0 ? hasMinutes : 0;
      const sec = Math.floor((time % 60 < 10 ? '' : '') + (time % 60));
      return { min, sec };
    },

    async sendData() {
      const {
        duration: elapsedTime,
        numberOfChars,
        numberOfNonLetters,
        errors: errorData
      } = this.getErrorLogger();

      const {
        activityId,
        grade,
        nextPosition: { asn, week }
      } = this;

      // add assessment data
      const progressData = {
        activityId,
        elapsedTime,
        numberOfChars,
        numberOfNonLetters,
        errorData,
        grade,
        asn,
        week
      };

      const { status, data } = await KeyboardingAPI.addProgressData(
        this.activityType,
        this.studentId,
        progressData
      );

      if (status < 400) {
        const { position } = data;
        this.setCurriculumPosition(position);
      } else {
        this.$store.commit('application/setModal', { type: 'error' }, { root: true });
      }
    },

    redoSpotCheck() {
      this.setSpotCheckRetries(this.spotCheckRetries - 1);
      this.restartActivity();
      this.$router.replace({ path: `/activities/${this.activityName}` });
    },

    continueCurriculum() {
      this.$router.replace({ path: '/activity-trophy', params: {} });
    }
  }
};
</script>

<style lang="scss" scoped>
.results-wrapper {
  position: relative;
  width: 1024px;
  height: 768px;
  background-color: #2e70be;
  text-align: center;
  .title {
    height: 180px;
    width: 1024px;
    text-align: center;
    h1 {
      line-height: 60px;
      margin: 0;
      padding: 0;
      text-align: center;
      font-size: 50px;
      color: white;
      font-family: hwtfutura-semibold;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      span {
        display: block;
      }
    }
  }
  .chart {
    margin: 0 auto;
    width: 817px;
    height: 434px;
    background-size: 817px 434px;
    position: relative;
    .spotCheckType {
      width: 607px;
      height: 60px;
      line-height: 72px;
      font-size: 24px;
      font-family: hwtfutura-semibold;
      margin: 0;
      position: absolute;
      top: -70px;
      text-align: center;
      left: -210px;
    }
    .myBestTitle {
      left: calc(607px - 210px);
      top: -70px;
      width: 205px;
      height: 60px;
      line-height: 72px;
      font-size: 24px;
      font-family: hwtfutura-semibold;
      margin: 0;
      position: absolute;
      text-transform: uppercase;
      text-align: center;
    }
    .stats-container {
      height: 60px;
      font-size: 24px;
      font-family: hwtfutura-semibold;
      margin: 0;
      position: absolute;
      left: 210px;
      top: 70px;
      text-align: left;
      ul.stats {
        &.border.more {
          margin-top: 25px;
        }

        list-style: none;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        text-align: left;
        margin: 0px;
        padding: 0px;

        li {
          vertical-align: top;
          font-family: hwtfutura-semibold;
          display: inline-block;
          font-size: 40px;
          margin: 10px 0;
          padding: 12px 0;

          display: flex;
          justify-content: space-between;

          span.category-spacer {
            column-gap: 4px;
            display: flex;

            span {
              display: flex;
              flex-direction: row;
              text-align: right;
            }
          }

          &:nth-child(odd) {
            border-bottom: 1px solid black;
          }
        }
      }

      ul.myBest {
        list-style: none;
        margin: 0px;
        padding: 0px;

        .heading {
          font-family: hwtfutura-semibold;
        }
      }

      .category {
        width: 331px;
        line-height: 40px;

        .smaller {
          font-size: 24px;
          line-height: 24px;

          span {
            display: block;
            font-size: 12px;
            line-height: 12px;
          }
        }
      }

      .statsValue {
        line-height: 40px;
        text-align: right;
        & div {
          display: inline-block;
        }
      }
      .statsValueDecorator {
        font-size: 16px;
        line-height: 50px;
        vertical-align: text-bottom;
      }
    }
  }
}
</style>
